/**
 * SearchableSelect — a type-to-filter dropdown replacement for <select>.
 *
 * Props:
 *   value             — currently selected option value (string/number)
 *   onChange          — called with the selected value string
 *   options           — [{ value, label }]
 *   placeholder       — text shown when nothing is selected
 *   disabled          — bool
 *   className         — extra classes on the wrapper
 *   emptyMessage      — text when no options match search
 *   onCreateNew       — optional callback when "+ Create new" is clicked
 *   createNewLabel    — label for the create-new row
 *
 * Server-side search mode (all optional, default off — backward compatible):
 *   onSearchChange    — called with the current search string whenever
 *                       the user types. Parent owns debouncing + fetching.
 *   serverSideSearch  — when true, SearchableSelect does NOT filter
 *                       `options` locally; the parent supplies pre-filtered
 *                       results via `options` after each onSearchChange.
 *   loading           — when true, a "Searching…" row renders at the top
 *                       of the dropdown body and replaces emptyMessage
 *                       when options is empty.
 */

import { useEffect, useRef, useState } from 'react';

export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  disabled = false,
  className = '',
  emptyMessage = 'No matches',
  onCreateNew = null,
  createNewLabel = '+ Create new',
  // NEW PROPS — all optional, fully backward compatible
  onSearchChange = null,
  serverSideSearch = false,
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  // Filter options by search text. Change A: in serverSideSearch mode
  // the parent is responsible for pre-filtering options server-side;
  // SearchableSelect just renders what the parent provides.
  const filtered = serverSideSearch
    ? options
    : (search.trim()
        ? options.filter((o) => {
            const q = search.toLowerCase();
            if (o.label.toLowerCase().includes(q)) return true;
            if (o.searchText && o.searchText.toLowerCase().includes(q)) return true;
            return false;
          })
        : options);

  // Change B: fire onSearchChange whenever the search string changes so
  // the parent can debounce + fetch. Debouncing is the parent's job;
  // this hook just propagates the raw value. ESLint disabled because
  // onSearchChange is intentionally captured at mount time — adding it
  // to deps would re-fire on identity churn from inline arrow callbacks.
  useEffect(() => {
    if (onSearchChange) onSearchChange(search);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click. Change D: behavior is identical for the
  // default (local-filter) path. For serverSideSearch the reset to ''
  // fires onSearchChange('') via the effect above so the parent can
  // return to its initial state.
  useEffect(() => {
    function handle(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  function handleSelect(optValue) {
    onChange(String(optValue));
    setOpen(false);
    setSearch('');
  }

  function handleClear(e) {
    e.stopPropagation();
    onChange('');
    setOpen(false);
    setSearch('');
  }

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        className={`
          w-full flex items-center justify-between gap-2
          border rounded-md px-3 py-1.5 text-sm text-left
          focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
          ${disabled
            ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-white text-gray-900 border-gray-300 hover:border-gray-400 cursor-pointer'
          }
        `}
      >
        <span className={`flex-1 truncate ${!selected ? 'text-gray-400' : ''}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {value && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => e.key === 'Enter' && handleClear(e)}
              className="text-gray-300 hover:text-gray-500 text-xs leading-none"
              title="Clear"
            >
              ✕
            </span>
          )}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          {/* Search input */}
          <div className="px-2 py-1.5 border-b border-gray-100">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type to search…"
              className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options list */}
          <ul className="max-h-52 overflow-y-auto py-1">
            {/* Change C: loading indicator. Prepended above the filtered
                list when a server-side search is in flight. When loading
                AND filtered is empty, this row replaces emptyMessage. */}
            {loading && (
              <li className="px-3 py-2 text-sm text-gray-400 italic flex items-center gap-2">
                <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>
                Searching…
              </li>
            )}
            {filtered.length === 0 ? (
              <>
                {!loading && (
                  <li className="px-3 py-2 text-sm text-gray-400 italic">{emptyMessage}</li>
                )}
                {onCreateNew && search.trim() && (
                  <li
                    onClick={() => { onCreateNew(search.trim()); setOpen(false); setSearch(''); }}
                    className="px-3 py-2 text-sm cursor-pointer text-blue-600 hover:bg-blue-50 font-medium border-t border-gray-100"
                  >
                    {createNewLabel}: "{search.trim()}"
                  </li>
                )}
              </>
            ) : (
              <>
                {filtered.map((opt) => (
                  <li
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`
                      px-3 py-1.5 text-sm cursor-pointer truncate
                      ${String(opt.value) === String(value)
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    {opt.label}
                  </li>
                ))}
                {onCreateNew && search.trim() && (
                  <li
                    onClick={() => { onCreateNew(search.trim()); setOpen(false); setSearch(''); }}
                    className="px-3 py-2 text-sm cursor-pointer text-blue-600 hover:bg-blue-50 font-medium border-t border-gray-100"
                  >
                    {createNewLabel}: "{search.trim()}"
                  </li>
                )}
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
